import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import anychart from 'anychart';
import { Row, Space, Card, Tooltip, Badge } from 'antd';
import Slider from '@mui/material/Slider'
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

import {v4 as uuidv4} from 'uuid';
import RangeSelector from "./StockChart/RangeSelector";
import {extractRangeProperties, rewindowChartData, setDataGrouping} from "./StockChart/tools";
import {stylePlot, styleScroller, styleLegend, finishDrawingChart} from "./StockChart/styles";
import { useScreenshot } from 'use-react-screenshot';
import Screenshot from "../Components/Screenshot";
import {fillSeriesFromConfig, handleSeriesItem} from "./StockChart/seriesUtils";
import useAnnotations from "./StockChart/useAnnotations";
import ButtonGroup from '@mui/material/ButtonGroup';
import {BarChartRounded, StackedLineChartRounded, SpeakerNotesOffRounded, MessageRounded,
TextRotateVerticalRounded, TextRotationNoneRounded, LightModeRounded, NightsStayRounded} from "@mui/icons-material";
import {StarOutlined, FireOutlined, FireFilled} from "@ant-design/icons";
import { useChartSetup } from "./StockChart/hooks";
import {
    setSelectedChartPoints,
    addSelectedChartPoint,
    resetAnnotationToRemove,
} from "../Store/slices/appState.slice";
import NivoBarChart from "./nivo/NivoBarChart";
import { useEnsureTickersLoaded } from "../hooks/useEnsureTickersLoaded";
import TickerChip from "../Components/TickerChip";
import AnnotationMenu from "./StockChart/AnnontationMenu";
import AnnotationConfig from "./StockChart/AnnotationConfig";
import {getDataPointsForAnchors, calculateChangeFromAnchors} from "./StockChart/interactionUtils";
import ChangesBar from "./StockChart/ChangesBar";



const getIndexOnClick = (event)=>{
    return event.seriesStatus?.[0].nearestPointToCursor.index
}


const listenForRangeChanges = (chart, selectedRange, setSelectedRange, callbackOnRangeChange)=>{
    if (selectedRange.start && selectedRange.end){
        chart.selectRange(selectedRange.start, selectedRange.end);
        // callbackOnRangeChange?.(selectedRange.start, selectedRange.end);
    }

    chart.listen('selectedrangechangefinish', (e) => {
        const newRange = {start: e.firstSelected, end: e.lastSelected};
        setSelectedRange(newRange);
        callbackOnRangeChange?.(newRange.start, newRange.end);
    });
}


const filterInRange = (data, selectedRange)=>{
    let seriesData = [...data];
    if (selectedRange.start){
        seriesData = seriesData.filter(point => new Date(point[0]).getTime() >= selectedRange.start);
    }
    if (selectedRange.end){
        seriesData = seriesData.filter(point => new Date(point[0]).getTime() <= selectedRange.end);
    }
    return seriesData
}


// Function to convert data series for bar chart display, focusing on returns within the selected range
const convertToBarData = (dataSeries, selectedRange) => {
    const keys = new Set();
    const customColors = {};
    const barData = dataSeries.map(plotConfig => {
        return plotConfig.data.map(seriesConfig => {
            // Assuming data is sorted by timestamp
            let seriesData = filterInRange(seriesConfig.data, selectedRange)
            if (seriesData.length < 2) return null; // Not enough data to calculate return
            const firstValue = seriesData[0][1]; // First value in the selected range
            const lastValue = seriesData[seriesData.length - 1][1]; // Last value in the selected range
            const returnValue = ((lastValue - firstValue) / firstValue) * 100; // Calculate return percentage
            keys.add(seriesConfig.name);
            customColors[seriesConfig.name] = seriesConfig.stroke;
            return {
                id: seriesConfig.name, // Use series name as 'x'
                [seriesConfig.name]: returnValue // Return calculated as 'value'
            };
        }).filter(item => item !== null); // Filter out any nulls from series with insufficient data
    }).flat().sort((a, b)=>a[Object.keys(a).filter(k=>k!=='id')[0]] - b[Object.keys(b).filter(k=>k!=='id')[0]]); // Flatten and sort by timestamp

    return {
        data: barData, keys: Array.from(keys), customColors
    }
};

const getSnapshotExposuresInWindow = (dataSeries, selectedRange) => {
    let tickerExposures = {};

    dataSeries.forEach(plot => {
        plot.data.forEach(series => {
            if (series?.hasSnapshot === true) {
                let seriesData = filterInRange(series.data, selectedRange);
                const snapshots = seriesData.map(x => x[2]);

                snapshots.forEach(snapshot => {
                    Object.entries(snapshot).forEach(([ticker, data]) => {
                        if (!tickerExposures[ticker]) {
                            tickerExposures[ticker] = {
                                weights: [],
                                avg: 0,
                                min: Infinity,
                                max: -Infinity
                            };
                        }

                        const weight = data.wgt || 0;
                        tickerExposures[ticker].weights.push(weight);
                        tickerExposures[ticker].min = Math.min(tickerExposures[ticker].min, weight);
                        tickerExposures[ticker].max = Math.max(tickerExposures[ticker].max, weight);
                    });
                });
            }
        });
    });

    // Calculate averages and handle tickers with no allocation
    Object.keys(tickerExposures).forEach(ticker => {
        const exposure = tickerExposures[ticker];
        exposure.avg = exposure.weights.reduce((sum, weight) => sum + weight, 0) / exposure.weights.length;

        // If a ticker was never allocated, set min to 0
        if (exposure.min === Infinity) {
            exposure.min = 0;
        }

        // If a ticker was never allocated, set max to 0
        if (exposure.max === -Infinity) {
            exposure.max = 0;
        }

        // Remove the weights array as it's no longer needed
        delete exposure.weights;
    });

    const result = Object.entries(tickerExposures).map(([ticker, exposure]) => {
        return {
            id: ticker,
            avg: exposure.avg * 100,
            min: exposure.min === Infinity ? 0 : exposure.min * 100,
            max: exposure.max === -Infinity ? 0 : exposure.max * 100
        };
    }).sort((a, b) => b.avg - a.avg);

    return result;
};


export const formatPlotScale = (plot, config)=>{
    if (config.yScale) {
        plot.yScale().comparisonMode(config.yScale);
      }
      if (config.stack){
          plot.yScale().stackMode('value');
      }
      if (config.yMax){
          plot.yScale().maximum(config.yMax);
      }
}


const RenderTickerExposures = ({ tickerExposures }) => {
    const { tickers } = useEnsureTickersLoaded(tickerExposures.map(x=>x.id));
    const [showTickers, setShowTickers] = useState(true);
    const [slideValue, setSlideValue] = useState(tickerExposures.length-1);
    const maxSlideValue = tickerExposures.length - 1;
    const tickersToShow = slideValue > 0 ? tickerExposures.slice(0, slideValue + 1) : tickerExposures;

    return (
        <Card bordered={true}
              title={'Snapshot Exposures'}
              extra={
                 <Space>
                     <Button onClick={()=>setShowTickers(!showTickers)}>{showTickers ? 'Hide' : 'Show'}</Button>
                     <Slider
                        style={{width: 200}}
                        onChange={(e, value)=>setSlideValue(value)}
                        value={slideValue}
                        min={0}
                        max={maxSlideValue}
                    />
                 </Space>
              }
        >
            {showTickers&&(
                <Row className={'p-3'} justify={'start'} wrap={true}>
                        {tickersToShow.map((exposure, index) => (
                            exposure.id !== 'custodial_cash' && (
                                    <Space>
                                        <TickerChip imgSize={30} titleSize={12} textSize={10}
                                                    ticker={tickers.find((x)=>x.symbol===exposure.id)}
                                        />
                                        <div style={{width: 50}}>
                                            {exposure.avg.toFixed(2)}%
                                        </div>
                                    </Space>
                            )
                        ))}
                </Row>
            )}
        </Card>
    )
}





/**
 *
 * @param dataSeries [
 *        {
 *        plot: <index>,
 *          data: [
 *            {
 *              name: <series name>,
 *              type: <series type> *'line', 'stepLine', 'area'*,
 *              data: <series data: [[<timestamp>, <series1Value>, ...], [...], ...]>,
 *              **Optional**
 *              yScale: <'percentage', 'log'>,
 *              stroke: <color>,
 *            }
 *         ]
 *       }
 * ]
 * @param width
 * @param height
 * @param title
 * @param xTicks {number} - Number of ticks to show on the x-axis
 * @param customSnapshotCallback {function} - Callback function to handle custom snapshot data
 * @param allowScreenshot {boolean} - Flag to determine if the screenshot button should be shown
 * @param _recursive {boolean} - Internal flag to determine if the chart is being rendered recursively
 * @param showRangeSelector {boolean} - Flag to determine if the range selector should be shown
 * @param allowChartInteractions {boolean} - Flag to determine if the chart should allow interactions
 * @param callbackOnCreateWindow {function} - Callback function to handle creating a new window.
 *                                            ...It needs to accept 'dataSeries' as an argument with the data
 *                                            ...for each series in the chart truncated to the selected range
 *
 * @returns {JSX.Element}
 * @constructor
 */
const StockChart = forwardRef(({ dataSeries,
                      width = '100%',
                      height = 600,
                      title = "",
                      xTicks=40,
                      allowScreenshot=true,
                      customSnapshotCallback=null,
                      showRangeSelector=true,
                      allowChartInteractions=true,
                      callbackOnCreateWindow=null,
                      callbackOnAnnotationSelect=null,
                      callbackOnSelectedPoints=null,
                      callbackOnRangeChange=null,
                      disableAnnotations=false,
                      disableTooltip=false,
                      xPadding=50,
                      yPadding=10,
                      extraToolsLeft=null,
                      extraToolsRight=null,
                      _recursive=false,
                      disableLegend=false,
                      theme='light'

                    }, ref) => {
    const dispatch = useDispatch();
    const chartContainerRef = useRef(null);
    const chartRef = useRef(null);
    const controllerRef = useRef(null);
    const secondControllerRef = useRef(null);
    const [baseId] = useState(uuidv4());
    const [containerId] = useState(`chart-container-${baseId}`); // Generate a unique ID for the container
    const [rangeProps, setRangeProps] = useState(undefined);// [min, max, years
    const [image, takeScreenshot] = useScreenshot();
    const selectedPoints = useSelector((state)=>state.appStates.selectedChartPoints);
    const annotationsToRemove = useSelector((state)=>state.appStates.annotationsToRemove);
    const [selectedAnnotationPoints, setSelectedAnnotationPoints] = useState(null);
    const [selectedChanges, setSelectedChanges] = useState(null);
    const [selectedAnnotation, setSelectedAnnotation] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [barView, setBarView] = useState(false);
    const [tooltipDisabled, setTooltipDisabled] = useState(disableTooltip);
    const [legendDisabled, setLegendDisabled] = useState(disableLegend);
    const [annotationsJson, setAnnotationsJson] = useState({});
    const [legendDirection, setLegendDirection] = useState('vertical');
    const [chartTheme, setChartTheme] = useState(theme);
    const [secondAnnotationsJson, setSecondAnnotationsJson] = useState({});
    const annotations = useAnnotations({controller: controllerRef, chartTheme});
    const secondAnnotations = useAnnotations({controller: secondControllerRef, chartTheme});

    const {
        lastHoveredTimestamp, setLastHoveredTimestamp, setLastHoveredPoint, selectedRange,
        setSelectedRange
    } = useChartSetup(dataSeries, customSnapshotCallback);


    const updateAnnotationsJson = ()=>{
        setAnnotationsJson(controllerRef.current.toJson());
        setSecondAnnotationsJson(secondControllerRef.current?.toJson());
    }

    useImperativeHandle(ref, () => ({
        selectRange: (start, end) => {
          if (chartRef.current) {
            try {
              setSelectedRange({start, end});
              chartRef.current.selectRange(start, end);
              callbackOnRangeChange?.(start, end);
            } catch(e) {
              console.log("Error selecting range: ", e);
            }
          }
        },
        clearAnnotations: () => {
          if (controllerRef.current) {
            controllerRef.current.removeAllAnnotations();
          }
          setAnnotationsJson(null);

          return ref?.current;
        },
        clearSelectedPoints: () => {
          dispatch(setSelectedChartPoints([]));
          return ref?.current;
        },
        drawAnnotation: (plotIndex) => {
          return !plotIndex || plotIndex === 0 ? annotations : secondAnnotations;
        },
        finishAnnotation: () => {
            updateAnnotationsJson()
        },
        rewindowData: (startDate, endDate) => rewindowChartData(dataSeries, startDate, endDate),
      }));


    const handleCloseMenu = () => {
        setIsEditMode(false);
        setSelectedAnnotation(null);
        setSelectedChanges(null);
    };

    const updateSelectedAnchorsAndPoints = (startDate, endDate)=>{
        const selectedPoints = getDataPointsForAnchors(dataSeries, startDate, endDate);
        if (selectedPoints.x1.length > 0 && selectedPoints.x1.length===selectedPoints.x2.length){
            setSelectedAnnotationPoints(selectedPoints);
            const changes = calculateChangeFromAnchors(selectedPoints);
            setSelectedChanges(changes.length>0?changes:null);
        }
    }

    const handleAnnotationChange = ({color, thickness, opacity, xRange, labelText}) => {
        selectedAnnotation.normal().stroke(color, thickness);
        selectedAnnotation.selected()?.fill?.({color, opacity: opacity < 0.9 ? opacity + 0.1 : 1})
        selectedAnnotation.selected().markers({
            enabled: true,
            type: 'circle', // You can change this to 'square', 'diamond', etc.
            size: 8, // Adjust the size as needed
            fill: 'rgba(13, 42, 76, 0.7)', // Change this to your desired color
            stroke: {
                color: '#000000', // Border color
                thickness: 1,
            },
        });
        selectedAnnotation.normal()?.fill?.({
            color,
            opacity
        });
        if (xRange && xRange?.[0] !== null){
            try {
                const start = xRange[0];
                const end = xRange[1] ?? xRange[0];
                selectedAnnotation?.xAnchor?.(start);
                selectedAnnotation?.secondXAnchor?.(end);
                updateSelectedAnchorsAndPoints(start, end);
            }catch(e){
                console.log("Handle Annotation Change Error: ", e)
            }
        }
        if (selectedAnnotation.getType() === 'label'){
            selectedAnnotation.text(labelText);
        }
        setAnnotationsJson(controllerRef.current.toJson());

    }

    const handleAnnotationSelect = (type) => {
        annotations.startAnnotation(type);
        setIsEditMode(true); // Automatically enable edit mode when selecting an annotation
    };


    const handleRemoveAnnotation = () => {
        annotations.removeAnnotation(selectedAnnotation);
        handleCloseMenu();
        setAnnotationsJson(controllerRef.current.toJson());
    };
    /**
     * Function to handle snapping to the value of a selected annotation
     * @param seriesName {string} - The name of the series to snap to
     */
    const handleSnapToValue = (seriesName) => {
        if (!selectedAnnotationPoints) return;

        try {
            // Find the x1 and x2 points for the given seriesName
            const x1Data = selectedAnnotationPoints.x1.find(point => point.seriesName === seriesName);
            const x2Data = selectedAnnotationPoints.x2.find(point => point.seriesName === seriesName);

            // Get the valueAnchor from x1
            const valueAnchor = x1Data ? x1Data.pct : null;
            // Get the secondValueAnchor from x2
            const secondValueAnchor = x2Data ? x2Data.pct : null;
            // If selectedAnnotation exists, update its anchors
            selectedAnnotation.valueAnchor(valueAnchor);
            if (secondValueAnchor !== null) {
                selectedAnnotation.secondValueAnchor(secondValueAnchor);
            }
            setAnnotationsJson(controllerRef.current.toJson());
        }
        catch (error) {
            console.error("Error snapping to value:", error);
        }
    };


    useEffect(() => {setRangeProps(extractRangeProperties(dataSeries))}, [dataSeries]);


    useEffect(() => {
        if (!dataSeries || dataSeries.length === 0) return;
        let chart = anychart.stock();
        chartRef.current = chart;
        const seriesList = [];
        let scrollerDataMapping;

        dataSeries.forEach((plotConfig, plotIndex) => {
              const plot = chart.plot(plotConfig.plot);
              stylePlot(plot, plotConfig, chartTheme); // Assume this function styles the plot as needed
              plotConfig.data.forEach((seriesConfig, seriesIndex) => {
                const {dataTable, mapping} = handleSeriesItem(seriesConfig.data, seriesConfig);
                const dataMapping = dataTable.mapAs(mapping);

                const series = fillSeriesFromConfig(seriesConfig, plot, dataMapping, lastHoveredTimestamp,
                    setLastHoveredTimestamp, setLastHoveredPoint, !!customSnapshotCallback, tooltipDisabled);
                seriesList.push({ series, plotIndex, data: seriesConfig.data });
                if (!scrollerDataMapping && !plotConfig?.disableScrollerChart){
                    scrollerDataMapping = dataTable.mapAs({value: 1});
                }
              });
              if (legendDisabled===false) {
                  styleLegend(plot, plotConfig ?? {}, legendDirection, chartTheme);
              }
              else {
                  plot.legend().enabled(false);
              }
              formatPlotScale(plot, plotConfig);
              listenForRangeChanges(chart, selectedRange, setSelectedRange, callbackOnRangeChange);
              if (!disableAnnotations) {
                  if (!controllerRef.current) {
                      controllerRef.current = plot.annotations();
                  }
                  if (annotationsJson){
                      controllerRef.current.fromJson(annotationsJson);
                  }
                  if (annotationsToRemove.length > 0) {
                     annotationsToRemove.forEach(annotation=>{
                            controllerRef.current.select(annotation);
                            const selectedAnnToRemove = controllerRef.current.getSelectedAnnotation();
                            selectedAnnToRemove.normal().stroke("#FF0000", 3, "5 2", "round");
                            controllerRef.current.removeAnnotation(selectedAnnToRemove);
                        });

                     setAnnotationsJson(controllerRef.current.toJson());
                     dispatch(resetAnnotationToRemove());
                  }

                  plot.listen('dblClick', (e) => {
                      const clickIndex = getIndexOnClick(e);
                      const items = seriesList.map(s=>{
                              return {
                                    name: s.series.name(),
                                    data: s.data?.[clickIndex] ?? []
                                }
                      });
                      const timestamp = items[0].data[0];
                      const newAnnotation = annotations.setVerticalLine({date: timestamp});
                      const point = {clickIndex, items, timestamp, annotation: newAnnotation}
                      dispatch(addSelectedChartPoint(point));
                      setAnnotationsJson(controllerRef.current.toJson());

                    });
                  }
            });
        chart.interactivity().zoomOnMouseWheel(false);
        chart.interactivity().scrollOnMouseWheel(false);
        chart.listen("annotationSelect", function(e){
            setSelectedAnnotation(e.annotation);
        });
        chart.listen("annotationDrawingFinish", function(e){
            setAnnotationsJson(controllerRef.current.toJson());
        });
        chart.listen("annotationUnselect", function(e){
            handleCloseMenu()

        });
        setDataGrouping({chart, enabled:true});

        if (scrollerDataMapping) {
            styleScroller(chart, scrollerDataMapping, dataSeries[0], chartTheme);
        }
        finishDrawingChart(chart, xTicks, chartContainerRef.current, title, xPadding, yPadding, chartTheme);

        return () => {
            if (chart) {
                chart.dispose();
                controllerRef.current = null;
            }
        }}, [dataSeries, containerId, title, xTicks, selectedRange, selectedPoints, barView, legendDirection, chartTheme]);

    useEffect(() => {
        if (callbackOnAnnotationSelect) callbackOnAnnotationSelect(selectedAnnotation);
    }, [selectedAnnotation]);

  return (
    <>
      <Card
        bordered={false}
        className={'gapless sunken-shadow-2'}
      >
          <div style={{maxWidth: 1200, margin: 'auto'}}>
              {selectedAnnotation && (
                <AnnotationConfig
                    annotation={selectedAnnotation}
                    onChange={handleAnnotationChange}
                    onRemove={handleRemoveAnnotation}
                    onClose={handleCloseMenu}
                />
            )}
            {selectedChanges && <ChangesBar percentageReturns={selectedChanges} onSnapToValue={handleSnapToValue} />}
          </div>
          {rangeProps && showRangeSelector ? (
              <div style={{paddingLeft: '60px'}}>
                  <RangeSelector
                        {...rangeProps}
                        onYearSelected={setSelectedRange}
                        baseId={baseId}
                        selectedRange={selectedRange}
                        onScreenshot={allowScreenshot ? ()=>takeScreenshot(chartContainerRef.current) : undefined}
                        extra={
                            <Space>
                                {extraToolsLeft}
                                <AnnotationMenu onSelectAnnotation={handleAnnotationSelect} />
                                <ButtonGroup
                                  size={'small'}
                                  color={'primary'}
                                  exclusive
                                  variant={'outlined'}
                                >
                                    <Tooltip title={'New Dark Mode'}>
                                        <Badge count={<FireFilled style={{color: '#FF3737'}}/>} offset={[-43, -2]}>
                                            <Button size={'small'} variant={chartTheme==='light'?'outlined':'contained'} onClick={()=>setChartTheme(chartTheme==='light'?'dark': 'light')}>

                                                {chartTheme==='light' ? <LightModeRounded/> : <NightsStayRounded/>}
                                            </Button>
                                        </Badge>
                                    </Tooltip>

                                    <Button size={'small'} variant={'outlined'} onClick={()=>setLegendDirection(legendDirection==='vertical'?'horizontal': 'vertical')}>
                                        {legendDirection==='vertical' ? <TextRotateVerticalRounded/> : <TextRotationNoneRounded/>}
                                    </Button>
                                    <Button size={'small'} variant={'outlined'}  onClick={()=>setTooltipDisabled(!tooltipDisabled)}>
                                        {!tooltipDisabled ? <MessageRounded/> : <SpeakerNotesOffRounded/>}
                                    </Button>
                                    <Button size={'small'} variant={'outlined'} onClick={()=>setBarView(!barView)}>
                                        {
                                            barView===true ? <BarChartRounded fontSize={'small'} /> : <StackedLineChartRounded fontSize={'small'} />
                                        }
                                    </Button>
                                </ButtonGroup>
                                {extraToolsRight}
                            </Space>
                    }
                  />
              </div>

        ) : null}
          {barView===true ? (
              <div style={{width: '100%', marginTop: 20}}>
                  {customSnapshotCallback && <RenderTickerExposures tickerExposures={getSnapshotExposuresInWindow(dataSeries, selectedRange)} />}
                  <div style={{height: height}}>
                      <NivoBarChart {...convertToBarData(dataSeries, selectedRange)} vertical={false} barPadding={0.2} mode={'stacked'} />
                  </div>
              </div>
          ) : <div className={'stock-chart'} id={containerId} ref={chartContainerRef} style={{ width, height }} />}
      </Card>
        {image && <Screenshot
            image={image}

        />}
    </>
  );
});

export default StockChart;
